import React, { useState, useEffect, memo } from 'react';
import { Card, Space, DatePicker, Button, Spin, Skeleton, message, Segmented, Statistic, Row, Col, Progress, Tooltip as AntTooltip } from 'antd';
import { MetricCardHeader } from './Shared/MetricCardHeader';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  Cell
} from 'recharts';
import { 
  QuestionCircleOutlined, 
  ClockCircleOutlined, 
  ReloadOutlined,
  DownloadOutlined,
  BookOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { getQuestionComplexityAnalysis } from '../../api/api';
import type { QuestionComplexityResponse } from '../../types/api-models';
import { 
  formatXAxisLabel, 
  formatTooltipLabel, 
  formatPeriodDisplay,
  getXAxisInterval,
  type AggregationType 
} from '../../utils/dateFormatters';
import { chartColors, chartConfig } from '../../utils/chartColors';

const { RangePicker } = DatePicker;

interface QuestionComplexityAnalysisProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

const QuestionComplexityAnalysis: React.FC<QuestionComplexityAnalysisProps> = memo(({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<QuestionComplexityResponse | null>(null);
  const [aggregation, setAggregation] = useState<AggregationType>('daily');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);

  useEffect(() => {
    if (!isExpanded) {
      // Collapsed view: use dashboard data (30-day snapshot)
      if (dashboardData?.question_complexity) {
        setData(dashboardData.question_complexity);
      }
    }
    // Expanded view: Don't auto-load, wait for user interaction
  }, [isExpanded, dashboardData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let fromDate: string;
      let toDate: string;
      
      if (aggregation === 'daily') {
        // For daily, use the selected date range
        const is30DayRange = dateRange[1].diff(dateRange[0], 'days') === 30;
        
        if (is30DayRange) {
          // Use exact 30 days from now to match dashboard
          const now = dayjs();
          const thirtyDaysAgo = now.subtract(30, 'days');
          fromDate = thirtyDaysAgo.format('YYYY-MM-DD');
          toDate = now.format('YYYY-MM-DD');
        } else {
          fromDate = dateRange[0].format('YYYY-MM-DD');
          toDate = dateRange[1].format('YYYY-MM-DD');
        }
      } else {
        // For weekly/monthly, use predefined periods (no date selection)
        // Use current date as end date, let backend determine the start date
        const now = dayjs();
        toDate = now.format('YYYY-MM-DD');
        
        if (aggregation === 'weekly') {
          // For weekly, use last 52 weeks
          const fiftyTwoWeeksAgo = now.subtract(52, 'weeks');
          fromDate = fiftyTwoWeeksAgo.format('YYYY-MM-DD');
        } else {
          // For monthly, use last 12 months
          const twelveMonthsAgo = now.subtract(12, 'months');
          fromDate = twelveMonthsAgo.format('YYYY-MM-DD');
        }
      }
      
      const response = await getQuestionComplexityAnalysis(
        aggregation,
        fromDate,
        toDate
      );
      setData(response);
    } catch (error) {
      console.error('Error fetching question complexity analysis:', error);
      message.error('Failed to load question complexity analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity.toLowerCase()) {
      case 'short':
        return '#52c41a';
      case 'medium':
        return '#faad14';
      case 'long':
        return '#ff4d4f';
      default:
        return '#1890ff';
    }
  };

  const getComplexityIcon = (complexity: string) => {
    switch (complexity.toLowerCase()) {
      case 'short':
        return <QuestionCircleOutlined />;
      case 'medium':
        return <BookOutlined />;
      case 'long':
        return <ThunderboltOutlined />;
      default:
        return <QuestionCircleOutlined />;
    }
  };

  const renderCollapsedView = () => {
    if (!data) return <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>No data available</div>;

    const { averageQuestionLength = 0, complexityDistribution = [] } = data;
    
    // Safety check for empty data
    if (!complexityDistribution || complexityDistribution.length === 0) {
      return <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>No question complexity data available</div>;
    }

    return (
      <div style={{ height: '100%' }}>
        <div style={{ marginBottom: 16 }}>
          <MetricCardHeader
            title="Avg Question Length"
            value={`${Number(averageQuestionLength.toFixed(2))} words`}
            trend={data.complexity_trend}
            subtitle="Words per question"
          />
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Statistic
              title={
                <span>
                  Short Questions
                  <AntTooltip 
                    title="Questions with 1-10 words"
                    color="var(--text-color, #000)"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                  </AntTooltip>
                </span>
              }
              value={complexityDistribution.find(c => c.complexity === 'Short')?.count || 0}
              prefix={<QuestionCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title={
                <span>
                  Long Questions
                  <AntTooltip 
                    title="Questions with 20+ words"
                    color="var(--text-color, #000)"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                  </AntTooltip>
                </span>
              }
              value={complexityDistribution.find(c => c.complexity === 'Long')?.count || 0}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
        </Row>

        <div style={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={complexityDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="complexity" />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => [value, 'Questions']}
                labelFormatter={(label) => `${label} Questions`}
              />
              <Bar dataKey="count">
                {complexityDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getComplexityColor(entry.complexity)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderExpandedView = () => {
    return (
      <div style={{ height: '100%' }}>
        {/* Controls Row - Following Activity Trend Pattern */}
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space wrap>
            <span style={{ fontSize: 13, color: '#666' }}>Aggregation:</span>
            <Segmented
              value={aggregation}
              onChange={(value) => {
                setAggregation(value as AggregationType);
                // No need to reset date range for weekly/monthly since they don't use date selection
              }}
              options={[
                { label: 'Daily', value: 'daily' },
                { label: 'Weekly', value: 'weekly' },
                { label: 'Monthly', value: 'monthly' },
              ]}
            />
            {aggregation === 'daily' ? (
              <>
                <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Date Range:</span>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      // Check if range is more than 90 days
                      const daysDiff = dates[1].diff(dates[0], 'days');
                      if (daysDiff > 90) {
                        message.warning('Date range cannot exceed 90 days');
                        return;
                      }
                      setDateRange([dates[0], dates[1]]);
                    }
                  }}
                  format="YYYY-MM-DD"
                  style={{ width: 260 }}
                  disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
                />
              </>
            ) : (
              <span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
                Showing last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
              </span>
            )}
          </Space>
          
          <Space>
            <Button type="primary" onClick={handleRefresh} loading={loading}>
              Apply Filters
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} disabled={!data}>
              Refresh
            </Button>
            <Button 
              icon={<DownloadOutlined />} 
              onClick={() => {
                // TODO: Implement export functionality
                message.info('Export functionality coming soon');
              }}
            >
              Export
            </Button>
          </Space>
        </Space>

        {/* Chart */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin size="large" tip="Loading question complexity analysis..." />
          </div>
        ) : !data ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view question complexity analysis</p>
            <p style={{ fontSize: 14 }}>Configure filters above and click "Apply Filters" to load data</p>
          </div>
        ) : (
          <>
            {/* Data content goes here */}
            {(() => {
              const { 
                averageQuestionLength, 
                complexityDistribution = [], 
                complexityVsResponseTime = [], 
                complexityTrend = [] 
              } = data;
              
              // Safety check for empty data
              if (!complexityDistribution || complexityDistribution.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
                    <p style={{ fontSize: 16, marginBottom: 8 }}>No question complexity data available</p>
                    <p style={{ fontSize: 14 }}>Try selecting a different date range or aggregation period</p>
                  </div>
                );
              }
              
              return (
                <>
                  {/* Summary Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic
              title="Average Question Length"
              value={Number(averageQuestionLength.toFixed(2))}
              suffix="words"
              prefix={<BookOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          {complexityDistribution.map((complexity, index) => (
            <Col span={6} key={index}>
              <Statistic
                title={`${complexity.complexity} Questions`}
                value={complexity.count}
                prefix={getComplexityIcon(complexity.complexity)}
                valueStyle={{ color: getComplexityColor(complexity.complexity) }}
                suffix={`(${complexity.percentage.toFixed(1)}%)`}
              />
            </Col>
          ))}
        </Row>

        {/* Question Complexity Distribution */}
        <Card title="Question Complexity Distribution" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={complexityDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="complexity" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [value, 'Questions']}
                      labelFormatter={(label) => `${label} Questions`}
                    />
                    <Bar dataKey="count">
                      {complexityDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getComplexityColor(entry.complexity)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ padding: '20px 0' }}>
                <h4>Complexity Breakdown</h4>
                {complexityDistribution.map((complexity, index) => (
                  <div key={index} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>
                        {getComplexityIcon(complexity.complexity)} {complexity.complexity} Questions
                      </span>
                      <span>{complexity.count} ({complexity.percentage.toFixed(1)}%)</span>
                    </div>
                    <Progress 
                      percent={complexity.percentage} 
                      strokeColor={getComplexityColor(complexity.complexity)} 
                      showInfo={false}
                    />
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Card>

        {/* Complexity vs Response Time */}
        {complexityVsResponseTime && complexityVsResponseTime.length > 0 && (
          <Card title="Complexity vs Response Time" style={{ marginBottom: 24 }}>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={complexityVsResponseTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="complexity" />
                  <YAxis 
                    label={{ value: 'Response Time (ms)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value}ms`, 'Avg Response Time']}
                    labelFormatter={(label) => `${label} Questions`}
                  />
                  <Bar dataKey="avgResponseTime">
                    {complexityVsResponseTime.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getComplexityColor(entry.complexity)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {/* Complexity Trend */}
        {complexityTrend && complexityTrend.length > 0 && (
          <Card title="Question Complexity Trend Over Time">
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={complexityTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => formatXAxisLabel(value, aggregation)}
                  />
                  <YAxis 
                    label={{ value: 'Average Complexity', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value}`, 'Avg Complexity']}
                    labelFormatter={(label) => formatTooltipLabel(label, aggregation)}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="avgComplexity" 
                    stroke="#1890ff" 
                    strokeWidth={2}
                    dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {/* Complexity Summary */}
        {/* <Card title="Question Complexity Summary" style={{ marginTop: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }}>
                  <BookOutlined />
                </div>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#222', marginBottom: 4 }}>
                  {averageQuestionLength.toFixed(2)}
                </div>
                <div style={{ color: '#666' }}>Average Length (words)</div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                  Across all questions
                </div>
              </div>
            </Col>
            {complexityDistribution.map((complexity, index) => (
              <Col span={8} key={index}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: 24, color: getComplexityColor(complexity.complexity), marginBottom: 8 }}>
                    {getComplexityIcon(complexity.complexity)}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: '#222', marginBottom: 4 }}>
                    {complexity.count}
                  </div>
                  <div style={{ color: '#666' }}>{complexity.complexity} Questions</div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                    {complexity.percentage.toFixed(1)}% of total
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Card> */}
                </>
              );
            })()}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: '100%' }}>
      {isExpanded ? renderExpandedView() : renderCollapsedView()}
    </div>
  );
});

QuestionComplexityAnalysis.displayName = 'QuestionComplexityAnalysis';

export default QuestionComplexityAnalysis;
